type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;

export const truthy = <T>(value: T): value is Truthy<T> => !!value;

export const camelToSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const snakeToCamel = (str: string) =>
    str.toLowerCase().replace(/([_][a-z])/g, (group) => group.toUpperCase().replace('_', ''));

export const isInteractiveElement = (event: Event) => {
    const { target } = event;

    // UCV uses shadowDOM, so the root element is always a div but with shadowRoot
    return (
        target instanceof Element && (['INPUT', 'TEXTBOX', 'TEXTAREA'].includes(target?.tagName) || target?.shadowRoot)
    );
};

export const cleanS3Url = (url: string) => {
    if (!url) {
        return '';
    }

    try {
        const parsedUrl = new URL(url);

        // List of pre-signed URL query parameters to remove
        const ignoredParams = [
            'X-Amz-Algorithm',
            'X-Amz-Credential',
            'X-Amz-Date',
            'X-Amz-Expires',
            'X-Amz-SignedHeaders',
            'X-Amz-Signature',
            'X-Amz-Security-Token',
            'AWSAccessKeyId',
            'Expires',
            'Signature',
        ];

        // Remove ignored query parameters
        ignoredParams.forEach((param) => parsedUrl.searchParams.delete(param));

        // Rebuild cleaned URL
        return parsedUrl.origin + parsedUrl.pathname;
    } catch (error) {
        console.error('Invalid URL:', url);

        return '';
    }
};

/**
 * Sanitize input into a format which is also suitable for textarea
 * @param {string} text
 */
export const sanitizeText = (text: string) => {
    const temp: string[] = [];

    for (const ch of text) {
        const cp = ch.codePointAt(0);

        // Line breaks
        if (cp === 0x000a || cp === 0x000d || cp === 0x2028 || cp === 0x2029) {
            temp.push('\n');
            continue;
        }

        // Space like symbols
        if (/\p{White_Space}/u.test(ch)) {
            temp.push(' ');
            continue;
        }

        if (
            (cp >= 0x0000 && cp <= 0x001f) || // C0 control
            (cp >= 0x007f && cp <= 0x009f) || // C1 control
            (cp >= 0x200c && cp <= 0x200f) || // format characters
            (cp >= 0x202a && cp <= 0x202e) || // bidi controls
            (cp >= 0x2060 && cp <= 0x206f) || // invisible operators
            (cp >= 0xfe00 && cp <= 0xfe0f) || // variation selectors
            (cp >= 0xe0100 && cp <= 0xe01ef) // VS17–VS256
        ) {
            continue;
        }

        temp.push(ch);
    }

    return temp.join('');
};
